import type { ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';

import { Icon } from '../icons/Icon';
import type { IconTone } from '../icons/Icon';
import type { IconName } from '../icons/iconNames';
import { Button } from '../primitives/Button';
import { Text } from '../primitives/Text';
import { useTheme } from '../theme/ThemeProvider';

export type EmptyStateProps = {
  icon?: IconName;
  iconTone?: IconTone;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void | Promise<void>;
  busy?: boolean;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export function EmptyState({
  icon,
  iconTone = 'muted',
  title,
  description,
  actionLabel,
  onAction,
  busy = false,
  children,
  style,
  testID,
}: EmptyStateProps) {
  const theme = useTheme();

  return (
    <View
      testID={testID}
      style={[styles.container, { gap: theme.spacing(3), padding: theme.spacing(6) }, style]}
    >
      {icon !== undefined ? <Icon name={icon} size="xl" tone={iconTone} /> : null}

      {title !== undefined ? (
        <Text variant="title" style={styles.centered}>
          {title}
        </Text>
      ) : null}

      {description !== undefined ? (
        <Text variant="muted" style={styles.centered}>
          {description}
        </Text>
      ) : null}

      {children}

      {actionLabel !== undefined ? (
        <Button
          title={actionLabel}
          variant="secondary"
          loading={busy}
          onPress={() => void onAction?.()}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  centered: { textAlign: 'center' },
});
